export default class ParticleEngine {
  constructor(controller) {
    this.controller = controller;
    this.particles = [];
    this.initializedFlows = new Set();
    this.edgePaths = new Map();
    this.animationFrame = null;
    this.lastFrameTime = 0;
    this.lastSpawnTime = null;
  }

  updateColors(styles) {
    this.solarColor = styles.getPropertyValue("--color-energy-fill-solar").trim();
    this.gridColor = styles.getPropertyValue("--color-energy-fill-fast").trim();
  }

  start() {
    if (!this.controller.ctx) return;

    this.lastFrameTime = 0;
    const frameInterval = 1000 / 30;

    const animate = (currentTime) => {
      this.animationFrame = requestAnimationFrame(animate);

      if (this.lastFrameTime === 0) {
        this.lastFrameTime = currentTime;
      }

      if (currentTime - this.lastFrameTime < frameInterval) return;
      this.lastFrameTime += frameInterval;

      this.update();
      this.render();
    };
    requestAnimationFrame(animate);
  }

  stop() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  reset() {
    this.particles = [];
    this.initializedFlows = new Set();
  }

  clearCanvas() {
    const ctx = this.controller.ctx;
    if (!ctx) return;
    const canvas = this.controller.canvasTarget;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  update() {
    this.spawn();

    this.particles = this.particles.filter((particle) => {
      particle.progress += particle.speed;

      particle.wobbleVelocity += (Math.random() - 0.5) * 0.3;
      particle.wobbleVelocity *= 0.95;
      particle.wobble += particle.wobbleVelocity;
      const maxW = particle.maxWobble || 8;
      particle.wobble = Math.max(-maxW, Math.min(maxW, particle.wobble));

      return particle.progress < 1;
    });
  }

  spawn() {
    if (!this.controller.flowsEnabledValue) return;
    if (!this.controller.actuals?.flows) return;

    const now = Date.now();
    if (this.lastSpawnTime && now - this.lastSpawnTime < 200) return;
    this.lastSpawnTime = now;

    const MAX_PARTICLES_PER_CYCLE = 5;

    const activeFlows = Object.entries(this.controller.actuals.flows).filter(
      ([flowKey, flow]) => Math.abs(flow.power_kw) > 0 && this.edgePaths.has(flowKey),
    );

    const maxFlowPower = Math.max(...activeFlows.map(([, flow]) => Math.abs(flow.power_kw)), 1);

    for (const [flowKey, flow] of activeFlows) {
      const pathData = this.edgePaths.get(flowKey);
      if (!pathData) continue;

      const targetId = flowKey.split("->")[1];
      const targetNode = this.controller.nodes.find((n) => n.id === targetId);
      const edgeMaxPower = targetNode?.max_power_kw || Math.abs(flow.power_kw);
      const edgeWidth = this.controller.treeRenderer.getEdgeWidth(edgeMaxPower);
      const maxWobble = edgeWidth * 0.75;

      const pixelsPerFrame = 3.0;
      const speedAsProgress = pathData.pathLength > 0 ? pixelsPerFrame / pathData.pathLength : 0.01;

      if (!this.initializedFlows.has(flowKey)) {
        const initialParticles = Math.max(3, Math.ceil(pathData.pathLength / 40));
        for (let i = 0; i < initialParticles; i++) {
          this.particles.push({
            flowKey,
            progress: Math.random(),
            speed: speedAsProgress,
            color: this.getParticleColor(flowKey),
            size: 1.5,
            wobble: (Math.random() - 0.5) * 2 * maxWobble,
            wobbleVelocity: 0,
            maxWobble,
          });
        }
        this.initializedFlows.add(flowKey);
      }

      const powerRatio = Math.abs(flow.power_kw) / maxFlowPower;
      const particlesToSpawn = Math.max(1, Math.round(MAX_PARTICLES_PER_CYCLE * powerRatio));

      for (let i = 0; i < particlesToSpawn; i++) {
        this.particles.push({
          flowKey,
          progress: (i / particlesToSpawn) * 0.05,
          speed: speedAsProgress,
          color: this.getParticleColor(flowKey),
          size: 1.5,
          wobble: (Math.random() - 0.5) * 2 * maxWobble,
          wobbleVelocity: 0,
          maxWobble,
        });
      }
    }
  }

  getParticleColor(flowKey) {
    const solarColor = this.solarColor || "#facc15";
    const gridColor = this.gridColor || "#60a5fa";

    if (flowKey.includes("pv") || flowKey.includes("solar")) {
      return solarColor;
    }

    if (flowKey.includes("->grid_source")) {
      return solarColor;
    }

    if (flowKey.includes("grid_source->")) {
      return gridColor;
    }

    const solarRatio = this.controller.actuals?.solar_ratio || 0;
    if (solarRatio > 0 && Math.random() < solarRatio) {
      return solarColor;
    }

    return gridColor;
  }

  render() {
    const ctx = this.controller.ctx;
    if (!ctx) return;

    const canvas = this.controller.canvasTarget;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const transform = this.controller.currentTransform;
    if (!transform) return;

    for (const particle of this.particles) {
      const pathData = this.edgePaths.get(particle.flowKey);
      if (!pathData) continue;

      const point = this.getPointOnPath(pathData, particle.progress);
      if (!point) continue;

      const nextPoint = this.getPointOnPath(pathData, Math.min(particle.progress + 0.01, 1));
      let wobbleX = 0,
        wobbleY = 0;
      if (nextPoint) {
        const dx = nextPoint.x - point.x;
        const dy = nextPoint.y - point.y;
        const len = Math.sqrt(dx * dx + dy * dy) || 1;
        wobbleX = (-dy / len) * particle.wobble;
        wobbleY = (dx / len) * particle.wobble;
      }

      const screenX = transform.applyX(point.x + wobbleX);
      const screenY = transform.applyY(point.y + wobbleY);

      if (this.controller.isDarkMode) {
        ctx.beginPath();
        ctx.arc(screenX, screenY, (particle.size + 3) * transform.k, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.globalAlpha = 0.15;
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(screenX, screenY, particle.size * transform.k, 0, Math.PI * 2);
      ctx.fillStyle = particle.color;
      ctx.globalAlpha = this.controller.isDarkMode ? 0.5 : 0.85;
      ctx.fill();
    }
  }

  getPointOnPath(pathData, progress) {
    if (pathData.isGridLine) {
      const t = pathData.reversed ? 1 - progress : progress;
      return {
        x: pathData.x1 + (pathData.x2 - pathData.x1) * t,
        y: pathData.y1 + (pathData.y2 - pathData.y1) * t,
      };
    }

    const { source, target, isConnector, reversed } = pathData;
    const t = reversed ? 1 - progress : progress;

    if (this.controller.isHorizontal) {
      const sourceX = source.x + this.controller.nodeWidth / 2;
      const targetX = isConnector ? target.x - 16 : target.x - this.controller.nodeWidth / 2;

      const midX = (sourceX + targetX) / 2;
      const x =
        Math.pow(1 - t, 3) * sourceX +
        3 * Math.pow(1 - t, 2) * t * midX +
        3 * (1 - t) * Math.pow(t, 2) * midX +
        Math.pow(t, 3) * targetX;
      const y =
        Math.pow(1 - t, 3) * source.y +
        3 * Math.pow(1 - t, 2) * t * source.y +
        3 * (1 - t) * Math.pow(t, 2) * target.y +
        Math.pow(t, 3) * target.y;

      return { x, y };
    } else {
      const sourceY = source.y + this.controller.nodeHeight / 2;
      const targetY = isConnector ? target.y - 16 : target.y - this.controller.nodeHeight / 2;

      const midY = (sourceY + targetY) / 2;
      const x =
        Math.pow(1 - t, 3) * source.x +
        3 * Math.pow(1 - t, 2) * t * source.x +
        3 * (1 - t) * Math.pow(t, 2) * target.x +
        Math.pow(t, 3) * target.x;
      const y =
        Math.pow(1 - t, 3) * sourceY +
        3 * Math.pow(1 - t, 2) * t * midY +
        3 * (1 - t) * Math.pow(t, 2) * midY +
        Math.pow(t, 3) * targetY;

      return { x, y };
    }
  }

  storeEdgePath(flowKey, source, target, isConnector, reversed) {
    const pathLength = this.calculatePathLength(source, target, isConnector);
    this.edgePaths.set(flowKey, { source, target, isConnector, reversed, pathLength });
  }

  storeGridLinePath(x1, y1, x2, y2, gridNodeId) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const pathLength = Math.sqrt(dx * dx + dy * dy);

    this.edgePaths.set(`grid_source->${gridNodeId}`, {
      isGridLine: true,
      x1,
      y1,
      x2,
      y2,
      reversed: false,
      pathLength,
    });

    this.edgePaths.set(`${gridNodeId}->grid_source`, {
      isGridLine: true,
      x1,
      y1,
      x2,
      y2,
      reversed: true,
      pathLength,
    });
  }

  calculatePathLength(source, target, isConnector) {
    let length = 0;
    let prevPoint = this.getPointOnPath({ source, target, isConnector, reversed: false }, 0);
    const steps = 20;

    for (let i = 1; i <= steps; i++) {
      const point = this.getPointOnPath({ source, target, isConnector, reversed: false }, i / steps);
      const dx = point.x - prevPoint.x;
      const dy = point.y - prevPoint.y;
      length += Math.sqrt(dx * dx + dy * dy);
      prevPoint = point;
    }

    return length;
  }
}
